import { fetchFeed, isConfigured, mapEpisode, parseFeed } from 'services/buzzsprout';
import { formatTime } from 'shared/utils/formatTime';
import {
  Episode,
  EpisodeEntry,
  EPISODES,
  PublishedEpisode,
  SyndicatedEpisode,
  UpcomingEpisode,
} from './episodes';

let cachedPublished: PublishedEpisode[] = [];
let cachedUpcoming: UpcomingEpisode[] = [];
let bootstrapPromise: Promise<void> | null = null;
let refreshPromise: Promise<void> | null = null;

export function bootstrap(): Promise<void> {
  if (!bootstrapPromise) bootstrapPromise = loadAndHydrate();
  return bootstrapPromise;
}

export function refresh(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = loadAndHydrate().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function loadAndHydrate(): Promise<void> {
  if (!isConfigured) {
    applyPartition([], EPISODES as UpcomingEpisode[]);
    return;
  }
  try {
    const xml = await fetchFeed();
    const rawItems = parseFeed(xml);
    const syndicated = rawItems.map(mapEpisode);
    const { published, upcoming } = partition(EPISODES, syndicated);
    applyPartition(published, upcoming);
  } catch (err) {
    console.error('[episodeRepository] feed load failed', err);
    applyPartition([], EPISODES as UpcomingEpisode[]);
  }
}

function applyPartition(published: PublishedEpisode[], upcoming: UpcomingEpisode[]): void {
  cachedPublished = published;
  cachedUpcoming = upcoming;
}

function partition(
  entries: EpisodeEntry[],
  syndicated: SyndicatedEpisode[],
): { published: PublishedEpisode[]; upcoming: UpcomingEpisode[] } {
  const byId = new Map<number, SyndicatedEpisode>();
  const byNumber = new Map<string, SyndicatedEpisode>();
  for (const s of syndicated) {
    byId.set(s.buzzsproutId, s);
    if (s.episodeNumber != null) byNumber.set(String(s.episodeNumber), s);
  }

  const published: PublishedEpisode[] = [];
  const upcoming: UpcomingEpisode[] = [];

  for (const entry of entries) {
    const isNumberless = entry.type && entry.type !== 'regular';
    const byTitle = isNumberless
      ? syndicated.find(
        (source) => source.title.includes(entry.title),
      )
      : undefined;
    const source =
      byNumber.get(entry.number)
      ?? (entry.buzzsproutId != null ? byId.get(entry.buzzsproutId) : undefined)
      ?? byTitle;

    if (source) {
      if (!entry.host || !entry.category || !entry.showNotes) {
        console.warn(
          `[episodeRepository] ${entry.number} matched RSS but is missing editorial content`,
        );
        upcoming.push(entry as UpcomingEpisode);
        continue;
      }
      published.push(buildPublishedEpisode(entry, source));
    } else {
      upcoming.push(entry as UpcomingEpisode);
    }
  }

  published.sort((a, b) => b.publishedDate.getTime() - a.publishedDate.getTime());
  return { published, upcoming };
}

function buildPublishedEpisode(
  entry: EpisodeEntry,
  source: SyndicatedEpisode,
): PublishedEpisode {
  return {
    number: entry.number,
    buzzsproutId: source.buzzsproutId,
    title: entry.title,
    description: entry.description,
    publishedDate: source.publishedDate,
    audioUrl: source.audioUrl,
    durationSeconds: source.durationSeconds,
    durationTimestamp: formatTime(source.durationSeconds),
    artworkUrl: entry.artworkUrl ?? source.artworkUrl,
    host: entry.host!,
    category: entry.category!,
    image: entry.image,
    showNotes: entry.showNotes!,
    titleHighlight: entry.titleHighlight,
    resources: entry.resources,
    type: entry.type ?? 'regular',
  };
}

export function getPublishedEpisodes(): PublishedEpisode[] {
  return cachedPublished;
}

export function getUpcomingEpisodes(): UpcomingEpisode[] {
  return cachedUpcoming;
}

export function getAllEpisodes(): Episode[] {
  return [...cachedPublished, ...cachedUpcoming];
}

export function getEpisodeByNumber(number: string): Episode | undefined {
  return (
    cachedPublished.find((ep) => ep.number === number) ??
    cachedUpcoming.find((ep) => ep.number === number)
  );
}

export function hasPublishedEpisodes(): boolean {
  return cachedPublished.length > 0;
}
